import type {
  FlattenRecords,
  IfAny,
  OptionalPropertiesOf,
  PublicInterfaceOf,
  Requires,
  RequiresExcept,
} from './core.ts';
import { pare } from './records.ts';
import type { TypeRange } from './type_equality.ts';
import { isArray, isObject } from './type_guards.ts';

// Idiom for preventing excessive recursion in types. Example:
//   type R<T, MD extends MaxDepth[number] = DefaultMaxDepth> = [MD] extends [never]
//     ? never
//     : (Type expression involving R<U, MaxDepth[MD]>)
type MaxDepth = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
type DefaultMaxDepth = 4;

// Makes all properties of T optional, recursively.
// Objects with private or protected fields are not recursed into.
export type DeepPartial<T extends object, MD extends MaxDepth[number] = DefaultMaxDepth> = [
  MD,
] extends [never]
  ? never
  : PublicInterfaceOf<T> extends T
    ? {
        [K in keyof T]?: T[K] extends object ? DeepPartial<T[K], MaxDepth[MD]> : T[K];
      }
    : T;
// export type DeepPartial<T> = T extends object
//   ? {
//       [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
//     }
//   : T;

// Pairwise recursive merge of the `D`ominant and `R`ecessive input types.
// For every leaf property keypath in either input (e.g. `.x.y` in `{ x: { y: string }}`), the
// result type has a property of the same type at the same keypath. When a keypath appears in _both_
// inputs, the dominant's property type is used.
export type DeepMerge<D extends object, R extends object> = Omit<D, keyof R> &
  Omit<R, keyof D> & {
    [K in keyof D & keyof R]: D[K] extends object
      ? R[K] extends object
        ? DeepMerge<D[K], R[K]>
        : D[K]
      : D[K];
  };

// https://stackoverflow.com/a/48218209
export function deepMerge(...args: object[]): unknown {
  return args.reduce<Record<string, unknown>>((current, next) => {
    Object.entries(next).forEach(([k, n]: [string, unknown]) => {
      if (n !== undefined) {
        const c = current[k];
        if (isArray(n)) {
          current[k] = n;
        } else if (isObject(n) && isObject(c)) {
          current[k] = deepMerge(c, n);
        } else {
          current[k] = n;
        }
      }
    });
    return current;
  }, {}); // as DeepMerge<objects.at(-1), DeepMerge<objects.at(-1), ... DeepMerge<objects[0]>>>;
}

export type SupersetKey<
  B,
  O,
  K extends keyof B & keyof O,
  MD extends MaxDepth[number],
> = B[K] extends object
  ? O[K] extends object
    ? [keyof B[K]] extends [SupersetKeys<B[K], O[K], MD>]
      ? K
      : never
    : K
  : K;

// The shared keys K of B and O for which B[K] is a superset of O[K].
// N.B. The depth limit is undesirable, but I'm not sure how to defer TS from trying to solve
// DeepOptional<B, O> in the general case (like in reflect.withDefaults), even though for concrete
// types it's easy to compute.
// https://github.com/microsoft/TypeScript/issues/26980#issuecomment-674465012
export type SupersetKeys<
  B extends object,
  O extends object,
  MD extends MaxDepth[number] = DefaultMaxDepth,
> =
  // N.B. `OptionalKey<T, U, keyof T & keyof U>` is equivalent, but much more expensive to compute.
  // SupersetKey<B, O, keyof B & keyof O>;
  [MD] extends [never]
    ? never
    : TypeRange<{ [K in keyof B & keyof O]: SupersetKey<B, O, K, MaxDepth[MD]> }>;

type BuildOptional<B extends object, O extends object, Ssk extends keyof B & keyof O> = Omit<
  B,
  keyof O
> & {
  [K in Ssk]?: undefined | (B[K] extends object ? DeepPartial<B[K]> : B[K]);
} & {
  [K in Exclude<keyof B & keyof O, Ssk>]: B[K] extends object
    ? O[K] extends object
      ? BuildOptional<B[K], O[K], SupersetKeys<B[K], O[K]>>
      : B[K]
    : B[K];
};

// Build a _partially_ optional version of `B`ase type, based on the `O`ptional type.
// The result type is identical to `B`, except that every leaf property keypath that exists in both
// `B` and `O` becomes optional. In addition, when an object property contain only such optional
// keys, it also becomes optional.
export type DeepOptional<
  B extends object,
  O extends DeepPartial<B>,
  MD extends MaxDepth[number] = DefaultMaxDepth,
> = BuildOptional<B, O, SupersetKeys<B, O, MD>>;

// Doesn't work.
// export type DeepOptional<B extends object, O extends object> = Omit<B, keyof O> & {
//   [K in keyof B & keyof O as B[K] extends object ? never : K]?: B[K];
// } & {
//   [K in keyof B & keyof O as B[K] extends object ? K : never]: B[K] extends object
//     ? O[K] extends object
//       ? DeepOptional<B[K], O[K]>
//       : B[K]
//     : B[K];
// };

export class Reflection<T extends object, O extends object> {
  constructor(private readonly defaults: object) {}

  // TODO: Allow dynamically applied defaults here.
  apply(options: O | undefined) {
    // Do not allow `provided` properties with value `undefined` to override `defaults`.
    // const filtered = pare(providedUntyped);
    // return Object.freeze(deepMerge(this.defaults, providedUntyped) as T);
    return Object.freeze(deepMerge(this.defaults, options ?? {}) as T);
  }
}

export type OptionsOf<R> = IfAny<
  R,
  never,
  [R] extends [Reflection<infer _T, infer O>] ? FlattenRecords<O> : never
>;

// An idiom for specifying a configuration options type, any default values for those options, and
// the resulting settings object from apply defaults to the options.
// Usage:
//   interface Settings {
//     a: number;
//     b: string;
//     c?: boolean;
//   };
//   const Settings = reflect<Settings>().withDefaults({
//     b: 'I'm a default',
//   });
//   type Options = OptionsOf<typeof Settings>; // N.B. Don't forget typeof!
//
//   function foo(options: Options) {
//     const settings = Settings.apply(options);
//   }
//
// In the above example, the type of `settings` is `Settings` and the type of `Options` is {
//   a: number;
//   b?: string | undefined;
//   c?: boolean;
// }
export function reflect<T extends object>() {
  return {
    withDefaults<D extends object, MD extends MaxDepth[number] = DefaultMaxDepth>(defaults: D) {
      // Without the depth limit on DeepOptional, this causes TS to recursive infinitely resulting
      // in either an error or OOM.
      type OptionsType = DeepOptional<T, D, MD>;
      return new Reflection<T, OptionsType>(defaults);
    },
  };
}

// Only the optional keys of `T`, but made required.
// Usage:
//   interface Options { ... };
//   const optionsDefaults: DefaultsFor<Options> = { ... }
//   const settings = optionsDefaults.apply(options);
export type DefaultsFor<T, Except extends keyof T = never> = Omit<
  Requires<OptionalPropertiesOf<T>>,
  Except
>;

// TODO: This really wants to define a combination of things:
// - An interface type for the options interface.
// - An object literal for the defaults of these options.
// - A function to combine them and produce settings.
// - A type for the settings value.
// Doing all of this requires changes to Typescript. Waiting on:
//   https://github.com/microsoft/TypeScript/issues/17574
/*
 * @deprecated Use reflect, instead.
 */
export function defaultsFor<T extends {}, Except extends keyof T = never>(
  defaults: DefaultsFor<T, Except>,
) {
  return {
    apply(provided: T) {
      // Do not allow `provided` properties with value `undefined` to override `defaults`.
      const filtered = pare(provided);
      // TODO: Figure out why this doesn't cast implicitly.
      return Object.freeze({ ...defaults, ...filtered }) as unknown as Readonly<
        RequiresExcept<T, Except>
      >;
    },
  };
}

// export function settingsFrom<T extends {}, U extends {}>(opt: T, def: U): T & U {
//   return { ...pare(opt), ...def };
// }
